// ─────────────────────────────────────────────────────────────────────────
// Fuente única de prioridad de sources + projection schemas + pointers.
//
// Backend `POST /products/query` resuelve cada field consultando sources en
// el orden declarado: la primera con valor no-vacío gana. Si quieres cambiar
// la prioridad (ej: priorizar vtex sobre sap), edita UN solo archivo:
// este. Si quieres que un field específico use solo `master`, sobrescríbelo
// en `LIST_PROJECTION_SCHEMA`.
//
// Convenciones:
// - `DEFAULT_SOURCES` aplica como fallback a todo field no declarado en el
//   `schema` del body. Si el catálogo de sources crece (ej: agregar 'wms'),
//   sumarlo acá.
// - `LIST_PROJECTION_SCHEMA` declara overrides per-field para el listado.
//   Solo los fields que requieren prioridad DISTINTA del default necesitan
//   estar acá. Mantenelo minimal — si todos los fields se resuelven con el
//   default, este objeto puede quedar vacío.
// - `DETAIL_DEFAULT_SOURCES` es lo que `getProduct` usa cuando NO viene
//   sourceSlug; con sourceSlug, se prepende y deduplica via `prependSource`.
// - `PENDING_POINTERS` es el set de JSON pointers que el dashboard "Pendientes"
//   evalúa como missing. NO es prioridad pero está acá porque combina con
//   los schemas anteriores (back chequea vacío en TODAS las sources
//   declaradas para cada pointer).
//
// Override por entorno:
// - `VITE_AVAILABLE_SOURCES` (CSV) acota los slugs permitidos para el
//   backend en uso. Si está set, las constantes exportadas se filtran a
//   ese subset al boot para evitar 400 "Sources inválidos en /foo: [vtex]"
//   cuando el back local no tiene la misma lista que prod. Vacío → no filtra.
// ─────────────────────────────────────────────────────────────────────────

import { env } from '@/lib/env';

// Set de slugs permitidos cuando el entorno los acota. `null` = no filtra.
const ALLOWED: ReadonlySet<string> | null =
  env.VITE_AVAILABLE_SOURCES.length > 0
    ? new Set(env.VITE_AVAILABLE_SOURCES)
    : null;

function filterSlugs(arr: readonly string[]): readonly string[] {
  if (!ALLOWED) return arr;
  return arr.filter((s) => ALLOWED.has(s));
}

// Recorre un projection-schema (nodos = objetos, hojas = string[]) y filtra
// cada hoja. Preserva la forma del input. Si una hoja queda vacía tras el
// filtro, queda como `[]` (el back interpreta vacío = usar default).
function filterSchema<T extends Record<string, unknown>>(obj: T): T {
  if (!ALLOWED) return obj;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
      out[k] = filterSlugs(v as string[]);
    } else if (v && typeof v === 'object') {
      out[k] = filterSchema(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out as T;
}

// Slugs canonical del catálogo. Si agregas una source (ej: 'wms'), agrégala
// acá y al final de DEFAULT_SOURCES (a menos que quieras priorizarla).
export const KNOWN_SOURCE_SLUGS = ['master', 'sap', 'axioma', 'vtex'] as const;
export type KnownSourceSlug = (typeof KNOWN_SOURCE_SLUGS)[number];

// Prioridad por default que aplica a TODO field no declarado en
// LIST_PROJECTION_SCHEMA o LIST_DEFAULT_SOURCES.
//
// La data que vivía en vtex se migró a master (excepto `media` y `pricing`),
// por eso vtex ya no aparece en los defaults: sumarla desperdicia una lookup
// en el back para fields que master ya cubre canónicamente. Vtex se declara
// per-field en LIST_PROJECTION_SCHEMA / DETAIL_PROJECTION_SCHEMA solo donde
// sigue siendo la fuente autoritativa.
export const DEFAULT_SOURCES: readonly string[] = filterSlugs([
  'master',
  'sap',
  'axioma',
]);

// Sources del body.default para el listado. Es el fallback de fields sin
// override per-field en LIST_PROJECTION_SCHEMA.
export const LIST_DEFAULT_SOURCES: readonly string[] = DEFAULT_SOURCES;

// Sources del body.default para el detalle (`POST /products/:sku/query`).
// `getProduct` prepende `sourceSlug` cuando viene desde un listado de otra
// fuente (ej: producto en axioma) para que esa source gane resolución.
// Vtex NO va en el default — solo aplica a media/pricing vía schema.
export const DETAIL_DEFAULT_SOURCES: readonly string[] = filterSlugs([
  'master',
  'sap',
  'axioma',
]);

// Projection schema del listado — declara CADA pointer que la tabla y el
// contador de pendientes necesitan renderizar.
//
// IMPORTANTE: `type: 'exact'` NO usa `default` como catch-all de proyección
// — solo devuelve los pointers listados aquí (+ id/sku obligatorios). Si un
// pointer no aparece, viaja como key ausente y `derivePendings` lo cuenta
// como missing → todo el listado queda con "+N pendientes" falso positivo.
// Verificado con Playwright: sin declarar name/brand/etc., el back devuelve
// {id, sku, media} solamente.
//
// - `media`: sap no maneja media. Master es canónico; axioma y vtex como
//   fallback (vtex conserva imágenes que ya estaban publicadas en e-commerce).
// - Resto: mismo orden que DEFAULT_SOURCES — declarados para forzar que el
//   back los proyecte, no por prioridad distinta.
//
// `attributes.*` declarados (2026-08-27) para bajar el costo de los filters
// winner-based: sin declarar, la resolución cae al fallback de 9 sources y
// las queries se disparan. `attributes.pricing` cae en el detail schema
// solo (vtex al final). Peer midió:
//   - `missingStock` sin declarar: ~5.2s. Con declaración: ~3.5s.
//   - `filters.isWebActive` idem — resuelve el pointer con prioridad de 3
//     sources en vez de 9. Impacto real cuando peer landeen su rewrite.
export const LIST_PROJECTION_SCHEMA: Record<string, unknown> = filterSchema({
  name: ['master', 'sap', 'axioma'],
  slug: ['master', 'sap', 'axioma'],
  isActive: ['master', 'sap', 'axioma'],
  brand: ['master', 'sap', 'axioma'],
  brandId: ['master', 'sap', 'axioma'],
  categoryId: ['master', 'sap', 'axioma'],
  categories: ['master', 'sap', 'axioma'],
  media: ['master', 'axioma', 'vtex'],
  attributes: {
    // `isWebActive` gana el que el source ganador reporte primero.
    isWebActive: ['master', 'sap', 'axioma'],
    // Inventory viaja con los OMS/ERP: sap primero.
    inventory: ['sap', 'master', 'axioma'],
    // Pricing: vtex al final por si un producto vive solo en el e-commerce.
    pricing: ['master', 'sap', 'axioma', 'vtex'],
  },
});

// Projection schema del detalle — override per-field para `getProduct`.
// Se pasa como `body.schema` (junto a `type: 'complete'`) para que fields
// específicos usen una prioridad distinta al DETAIL_DEFAULT_SOURCES.
//
// - `attributes.pricing`: vtex al final. El e-commerce sigue siendo la fuente
//   autoritativa de precios (basePrice/listPrice) — master no los mantiene.
export const DETAIL_PROJECTION_SCHEMA: Record<string, unknown> = filterSchema({
  attributes: {
    pricing: ['master', 'sap', 'axioma', 'vtex'],
  },
});

// Helper para `getProduct`: prepende sourceSlug al front de la lista y
// deduplica preservando orden. Se exporta acá (en vez de inline) para que el
// flow de "abrir producto desde axioma → priorizar axioma" sea testeable.
export function prependSource(sources: readonly string[], slug?: string): string[] {
  if (!slug || slug === 'master') return [...sources];
  return [slug, ...sources].filter((s, i, arr) => arr.indexOf(s) === i);
}

// ─────────────────────────────────────────────────────────────────────────
// Pendientes (dashboard tile + drill-down `/pending`)
// ─────────────────────────────────────────────────────────────────────────

// Pointers que el dashboard chequea como "missing" para contar productos
// pendientes. Cada uno corresponde a un field del canonical schema.
// Si agregas un pointer acá, también va a aparecer en el contador anyMissing
// del dashboard y en el drill-down (si el `?check=` mapea a ese pointer).
export const PENDING_POINTERS: readonly string[] = ['/name', '/brand', '/categories', '/media'];

// Sources que el back evalúa al determinar si un pointer está "vacío" en
// missingPointers. Se aplica como `default` del body de las requests del
// dashboard pending. Diferente de LIST_DEFAULT_SOURCES — acá sumamos vtex
// para no marcar como missing un `/media` que sí existe en vtex (que sigue
// siendo fuente canónica de media/pricing).
export const PENDING_DEFAULT_SOURCES: readonly string[] = filterSlugs([
  'master',
  'sap',
  'axioma',
  'vtex',
]);

// ─────────────────────────────────────────────────────────────────────────
// Playground (/products-query)
// ─────────────────────────────────────────────────────────────────────────

// Body inicial del playground en modo "lista". El user puede editarlo en
// vivo desde la UI; estos son solo los defaults de ejemplo.
// Campos elegidos por lo que se consulta de verdad: identificar el producto
// (nombre), verlo (media), y los tres datos que más se revisan por faltantes
// (compatibilidad, precio, inventario).
//
// Prioridades: master primero como fuente curada, sap después como sistema de
// origen. vtex entra SOLO en media, y va última: mismo orden que
// `LIST_PROJECTION_SCHEMA` acá arriba, que es lo que resuelve el listado de
// productos. Con vtex adelante, el Lab mostraría imágenes distintas a las de
// la pantalla que intenta explicar.
//
// Precio e inventario se resuelven solo con master y sap, las dos fuentes que
// los mantienen: axioma y vtex se sacaron a pedido del dueño del producto.
//
// `isWebActive: true` por defecto: el listado arranca mostrando lo que está
// publicado, que es el caso que se mira a diario. Al buscar por SKU el filtro
// se saca (ver `withoutWebActiveFilter`), igual que en el buscador de
// productos: si alguien pega un SKU quiere ESE producto, publicado o no.
//
// `sourceSlugs` NO va: no filtra campos, acota qué FILAS de fuente se cargan,
// así que listar las escritoras ahí equivale a no filtrar (las otras seis
// fuentes tienen cero filas de producto) y dejar a vtex afuera volvería muerta
// su prioridad en media.
export const PLAYGROUND_LIST_QUERY = {
  type: 'exact' as const,
  default: filterSlugs(['master', 'sap', 'axioma']),
  schema: filterSchema({
    name: ['master', 'sap', 'axioma'] as readonly string[],
    media: ['master', 'axioma', 'vtex'] as readonly string[],
    attributes: {
      applicationModels: ['master', 'sap'] as readonly string[],
      pricing: ['master', 'sap'] as readonly string[],
      inventory: ['master', 'sap'] as readonly string[],
    },
  }),
  filters: { isWebActive: true },
};

// Body inicial del playground en modo "detalle".
export const PLAYGROUND_DETAIL_QUERY = {
  type: 'complete' as const,
  default: DETAIL_DEFAULT_SOURCES,
  schema: {} as Record<string, readonly string[]>,
  filters: {},
};
